<script lang="ts">
	import { DataHandlerFolder, type Folder, LightshowBreak, type Media, Scene } from "luxedo-data"
	import UrlBar from "../../../reusable/file-system/UrlBar.svelte"
	import SearchBarInput from "../../../reusable/input/SearchBarInput.svelte"
	import { Toast } from "toaster"
	import { FileSystemMedia } from "../../../../modules/file-system/FileSystemMedia"
	import { MediaLibraryController } from "../MediaLibraryController"
	import { getCurrentUser } from "../../../../stores/UserStore.svelte"
	import MediaLibraryFilter from "./MediaLibraryFilter.svelte"
	import { Responsive } from "responsive"
	import { CTXOptions } from "../../../../modules/ctx-menu/ContextMenuOptions"

	interface Props {
		searchInput?: string
	}

	let { searchInput = $bindable(undefined) }: Props = $props()

	let isSearchOpen: boolean = $state(false)
	let isMobile = $derived(Responsive.isMobile)

	let activeFolder: Folder
	let path: Array<{ id: number; name: string }> = $state()
	let root: { id: number; name: string } = {
		id: getCurrentUser().directories.media,
		name: "My Media",
	}

	MediaLibraryController.subscribe((ctx) => {
		path = ctx.path
		activeFolder = ctx.activeFolder
	})

	function onSearch(searchValue: string) {
		searchInput = searchValue
	}

	function onCancelSearch() {
		searchInput = undefined
	}

	/**
	 * Handles when a dragged object is dropped onto a path element
	 * @param e DragEvent object (contains dataTransfer data)
	 * @param id Id of the folder being dropped onto
	 */
	async function handleDrop(
		e: DragEvent,
		content: Folder | Media | Scene | LightshowBreak,
		moveToId: number
	) {
		e.preventDefault()

		if (content instanceof Scene || content instanceof LightshowBreak) return

		try {
			const folder = DataHandlerFolder.get(moveToId)
			await FileSystemMedia.move(content, folder)
			Toast.success("File moved successfully.")
		} catch (error) {
			Toast.error(`Unable to move file, please try again later.`)
		}
	}

	function highlightFolder(id: number) {
		const folder = DataHandlerFolder.get(id)
		CTXOptions.triggerRename(folder)

		const folderTile = document.getElementById(`folder-tile-${id}`)
		const label = folderTile.querySelector(".tile-label-container .tile-label") as HTMLSpanElement

		setTimeout(() => {
			folderTile.classList.add("focused")
		})
		setTimeout(() => {
			folderTile.classList.remove("focused")
		}, 2500)
	}

	async function createFolder() {
		const id = await DataHandlerFolder.createEntry({
			name: "New Folder",
			parent_id: activeFolder.id,
		})
		await FileSystemMedia.refreshData()
		setTimeout(() => {
			highlightFolder(id)
		})
	}
</script>

<div id="library-options" class:isMobile class:isSearchOpen>
	<div class="header">
		<h3>My Media</h3>
		<div class="buttons">
			<button class="create-button outline-button alt" onclick={createFolder}>Create Folder</button>
			<button
				class="create-button outline-button"
				onclick={MediaLibraryController.openNewMediaOverlay}>Upload Media</button
			>
		</div>
	</div>
	<div class="filters">
		<UrlBar
			{root}
			{path}
			updateActiveDirectory={MediaLibraryController.updateActiveDirectory}
			{handleDrop}
		/>
		<MediaLibraryFilter />
		<SearchBarInput bind:isOpen={isSearchOpen} onClose={onCancelSearch} {onSearch} />
	</div>
</div>

<style>
	h3 {
		font-size: var(--h1);
		margin: 0;
		color: var(--color-text-light);
	}

	.create-button {
		margin-left: 1rem;
		padding: 0rem 1rem;
	}

	#library-options {
		display: flex;
		flex-direction: row;
		padding: 1rem;
		background-color: var(--color-bg-dark);
		border-radius: var(--br);
		align-items: center;
		white-space: nowrap;

		position: sticky;
		top: 0;
		z-index: 10;
		box-shadow: var(--shadow-small);
		width: 100%;
	}

	#library-options.isMobile {
		border-radius: unset;
		position: relative;
	}

	#library-options :global(.current-path) {
		margin: 0 1rem;
	}

	#library-options.isMobile {
		flex-direction: column;
		align-items: flex-start;
	}

	.header,
	.filters,
	.buttons {
		display: flex;
		flex-direction: row;
		align-items: center;
	}

	.header,
	.filters {
		flex-grow: 1;
	}

	.isMobile .header,
	.isMobile .filters,
	.isMobile .buttons {
		gap: 0.5rem;
	}

	.isMobile .filters {
		overflow: hidden;
		margin-top: 0.5rem;
		width: 100%;
	}

	.isMobile button {
		font-size: var(--h3);
		margin: 0;
	}

	#library-options.isMobile :global(.current-path) {
		margin: 0;
	}

	.isMobile :global(.media-filter) {
		margin-right: 0;
	}

	.isMobile .header {
		width: 100%;
	}

	.isMobile .buttons {
		margin-left: auto;
	}

	.isMobile.isSearchOpen .filters :global(.current-path),
	.isMobile.isSearchOpen .filters :global(.media-filter) {
		display: none;
	}

	.isMobile :global(.search-container) {
		margin-left: auto;
	}

	.isMobile .filters :global(.current-path) {
		flex-grow: 1;
	}
</style>
